import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STAGE_LABELS, STAGES_ORDER, currency } from "@/lib/constants";
import SLABadge from "@/components/SLABadge";
import SavedViewsMenu from "@/components/SavedViewsMenu";
import { toast } from "sonner";
import { Grid3x3, Rows, Download, Users, Tag, X, Sparkles, Send } from "lucide-react";

const VIEWS = { grid: "Grid", kanban: "Kanban" };

export default function Pipeline() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [view, setView] = useState("grid");
  const [params] = useSearchParams();
  const [filter, setFilter] = useState({ stage: "", department: "", search: params.get("search") || "", timeline: "", compliance_cleared: "" });
  const [viewFilters, setViewFilters] = useState({}); // From saved view (client-side extras)
  const [selected, setSelected] = useState({});
  const [team, setTeam] = useState([]);
  const [bulkAnalyst, setBulkAnalyst] = useState("");
  const [bulkAttorney, setBulkAttorney] = useState("");
  const [bulkTag, setBulkTag] = useState("");

  const canBulk = ["general_counsel", "legal_manager", "it_admin"].includes(user?.role);
  const canBulkCompliance = ["compliance", "general_counsel", "it_admin"].includes(user?.role);

  const load = () => {
    const p = {};
    if (filter.stage || viewFilters.stage) p.stage = filter.stage || viewFilters.stage;
    if (filter.department) p.department = filter.department;
    if (filter.search) p.search = filter.search;
    if (filter.compliance_cleared) p.compliance_cleared = filter.compliance_cleared;
    if (viewFilters.assigned_to) p.assigned_to = viewFilters.assigned_to;
    api.get("/contracts", { params: p }).then(r => setContracts(r.data)).catch(e => console.error(e));
  };

  useEffect(() => { load(); }, [filter, viewFilters]);
  useEffect(() => { api.get("/users/team").then(r => setTeam(r.data)).catch(() => {}); }, []);

  const applyView = (f) => {
    setViewFilters(f || {});
    setFilter({ stage: f?.stage || "", department: "", search: "", timeline: "" });
  };

  const filtered = useMemo(() => {
    let list = contracts;
    if (filter.timeline) list = list.filter(c => c.timeline === filter.timeline);
    if (viewFilters.sla) list = list.filter(c => c.sla_status === viewFilters.sla);
    if (viewFilters.renewalsWithinDays != null) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() + viewFilters.renewalsWithinDays);
      list = list.filter(c => (c.red_flags || []).some(f => {
        if (!f.renewal_date) return false;
        const d = new Date(f.renewal_date);
        return d > new Date() && d <= cutoff;
      }));
    }
    return list;
  }, [contracts, filter, viewFilters]);

  const byStage = useMemo(() => {
    const g = {};
    STAGES_ORDER.forEach(s => g[s] = []);
    filtered.forEach(c => { (g[c.stage] || (g[c.stage] = [])).push(c); });
    return g;
  }, [filtered]);

  const selectedIds = Object.keys(selected).filter(id => selected[id]);
  const selectedCount = selectedIds.length;

  const toggleRow = (id, e) => { e?.stopPropagation?.(); setSelected(prev => ({ ...prev, [id]: !prev[id] })); };
  const toggleAll = () => {
    if (selectedCount === filtered.length) setSelected({});
    else setSelected(Object.fromEntries(filtered.map(c => [c.id, true])));
  };
  const clearSelection = () => setSelected({});

  const selectAllUncleared = () => {
    const target = filtered.filter(c => c.stage === "executed" && !(c.compliance_cleared?.cleared));
    if (target.length === 0) return toast.error("No executed + not-cleared contracts in the current view.");
    setSelected(Object.fromEntries(target.map(c => [c.id, true])));
    toast.success(`Selected ${target.length} executed + not-yet-cleared`);
  };

  const exportCSV = (idsOnly = false) => {
    const list = idsOnly ? filtered.filter(c => selected[c.id]) : filtered;
    const cols = ["contract_number", "vendor_legal_name", "department", "stage", "timeline", "days_total", "contract_amount", "sla_status", "initial_received_date", "executed_date"];
    const rows = [cols.join(",")].concat(list.map(c => cols.map(k => `"${(c[k] ?? "").toString().replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = idsOnly ? "contracts_selected.csv" : "contracts.csv";
    a.click();
  };

  const bulkAssign = async () => {
    if (!bulkAnalyst && !bulkAttorney) return toast.error("Pick an analyst or attorney");
    try {
      const { data } = await api.post("/contracts/bulk-assign", {
        contract_ids: selectedIds,
        analyst_id: bulkAnalyst || undefined,
        attorney_id: bulkAttorney || undefined,
      });
      toast.success(`Assigned ${data.modified} contract${data.modified !== 1 ? "s" : ""}`);
      setBulkAnalyst(""); setBulkAttorney(""); clearSelection(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk assign failed"); }
  };

  const bulkTagApply = async (remove = false) => {
    if (!bulkTag.trim()) return toast.error("Enter a tag");
    try {
      const { data } = await api.post("/contracts/bulk-tag", { contract_ids: selectedIds, tag: bulkTag.trim(), remove });
      toast.success(`${remove ? "Removed" : "Applied"} tag to ${data.modified} contracts`);
      setBulkTag(""); clearSelection(); load();
    } catch (e) { toast.error("Bulk tag failed"); }
  };

  const bulkComplianceClear = async () => {
    const executed = filtered.filter(c => selected[c.id] && c.stage === "executed");
    if (executed.length === 0) return toast.error("Only Executed contracts can be Compliance Cleared. None selected are executed.");
    const notes = window.prompt(`Bulk Compliance Clear ${executed.length} executed contract(s). Optional notes:`, "") || "";
    try {
      const { data } = await api.post("/contracts/batch-compliance-clear",
        { contract_ids: executed.map(c => c.id), notes });
      const msg = `Stamped ${data.stamped}. Skipped ${data.skipped_already_cleared} already-cleared, ${data.skipped_missing} missing.`;
      toast.success(msg);
      clearSelection(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Batch clear failed"); }
  };

  const currentFilters = { ...filter, ...viewFilters };
  const [aiOpen, setAiOpen] = useState(false);
  const [aiQuery, setAiQuery] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const AI_PROMPTS = [
    "Which contracts should I prioritize this week and why?",
    "Which contracts are stuck in the same stage for more than 7 days?",
    "List all High-severity red flags across the pipeline",
    "Which vendors have upcoming auto-renewals in the next 60 days?",
    "Which contracts are overdue and what is the likely cause?",
  ];
  const askAI = async (q) => {
    const question = (q || aiQuery).trim();
    if (!question) return;
    setAiBusy(true); setAiAnswer("");
    try {
      const { data } = await api.post("/ai/query", { question, scope: "org" });
      setAiAnswer(data.answer || "No answer.");
    } catch (e) {
      setAiAnswer("AI is temporarily unavailable. Try again in a moment.");
    } finally { setAiBusy(false); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="pipeline-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Routing Pipeline</h1>
          <p className="text-sm text-slate-500 mt-1">Legal team tracker with SLA + per-person day counters.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAiOpen(true)}
                  className="h-9 px-3 rounded-md text-white text-xs font-semibold flex items-center gap-1.5"
                  style={{ background: "#7C3AED" }} data-testid="pipeline-ai-btn">
            <Sparkles className="w-3.5 h-3.5" /> AI Assistant
          </button>
          <SavedViewsMenu userId={user?.id} currentFilters={currentFilters} onApply={applyView} />
          <button onClick={() => exportCSV(false)} className="h-9 px-3 border border-slate-200 rounded-md text-xs font-semibold hover:bg-slate-50 flex items-center gap-1.5" data-testid="export-csv-btn"><Download className="w-3.5 h-3.5" />CSV</button>
          <div className="flex bg-slate-100 rounded-md p-0.5">
            {Object.entries(VIEWS).map(([k, label]) => (
              <button key={k} onClick={() => setView(k)}
                      className={`px-3 py-1.5 text-xs font-semibold rounded ${view === k ? "bg-white shadow-sm" : "text-slate-600"}`}
                      data-testid={`view-${k}`}>
                {k === "grid" ? <Rows className="w-3.5 h-3.5 inline mr-1" /> : <Grid3x3 className="w-3.5 h-3.5 inline mr-1" />}
                {label}
              </button>
            ))}
          </div>
          <button onClick={() => nav("/intake")} className="h-9 px-3 rounded-md text-white text-xs font-semibold" style={{ background: "#00AE42" }}>+ New Intake</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex gap-3 items-center">
        <input placeholder="Search vendor / #" className="h-8 px-2 text-sm border border-slate-200 rounded" value={filter.search} onChange={e => setFilter({ ...filter, search: e.target.value })} data-testid="filter-search" />
        <select value={filter.stage} onChange={e => setFilter({ ...filter, stage: e.target.value })} className="h-8 px-2 text-sm border border-slate-200 rounded" data-testid="filter-stage">
          <option value="">All stages</option>
          {STAGES_ORDER.map(s => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
        </select>
        <select value={filter.timeline} onChange={e => setFilter({ ...filter, timeline: e.target.value })} className="h-8 px-2 text-sm border border-slate-200 rounded">
          <option value="">All timelines</option>
          <option>21-day</option><option>40-day</option><option>Expedited</option>
        </select>
        <select value={filter.compliance_cleared} onChange={e => setFilter({ ...filter, compliance_cleared: e.target.value })} className="h-8 px-2 text-sm border border-slate-200 rounded" data-testid="filter-compliance-cleared">
          <option value="">All (Compliance)</option>
          <option value="yes">Compliance Cleared</option>
          <option value="no">Not yet cleared</option>
        </select>
        {canBulkCompliance && (
          <button onClick={selectAllUncleared}
                  className="h-8 px-3 text-xs font-semibold rounded border border-emerald-300 text-emerald-800 hover:bg-emerald-50 flex items-center gap-1"
                  data-testid="select-uncleared-btn"
                  title="Select every executed contract in the current view that isn't yet stamped Compliance Cleared">
            ✓ Select executed & uncleared
          </button>
        )}
        <div className="ml-auto text-xs text-slate-500">{filtered.length} contract{filtered.length !== 1 && "s"}</div>
      </div>

      {/* Bulk action toolbar */}
      {selectedCount > 0 && canBulk && view === "grid" && (
        <div className="bg-[#005289] text-white rounded-md p-3 flex items-center gap-3 flex-wrap" data-testid="bulk-toolbar">
          <div className="flex items-center gap-2 pr-3 border-r border-blue-400">
            <span className="text-xs font-bold">{selectedCount} selected</span>
            <button onClick={clearSelection} className="text-blue-200 hover:text-white" data-testid="bulk-clear">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            <select value={bulkAnalyst} onChange={e => setBulkAnalyst(e.target.value)} className="h-7 px-2 text-xs bg-white text-slate-900 border-none rounded" data-testid="bulk-analyst">
              <option value="">Assign Analyst...</option>
              {team.filter(t => t.role === "contract_analyst").map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select value={bulkAttorney} onChange={e => setBulkAttorney(e.target.value)} className="h-7 px-2 text-xs bg-white text-slate-900 border-none rounded" data-testid="bulk-attorney">
              <option value="">Assign Attorney...</option>
              {team.filter(t => t.role === "attorney").map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <button onClick={bulkAssign} className="h-7 px-3 bg-white text-[#005289] rounded text-xs font-bold" data-testid="bulk-assign-btn">Assign</button>
          </div>
          <div className="flex items-center gap-1.5 pl-3 border-l border-blue-400">
            <Tag className="w-3.5 h-3.5" />
            <input value={bulkTag} onChange={e => setBulkTag(e.target.value)} placeholder="Tag..." className="h-7 px-2 text-xs bg-white text-slate-900 border-none rounded w-32" data-testid="bulk-tag-input" />
            <button onClick={() => bulkTagApply(false)} className="h-7 px-2 bg-white text-[#005289] rounded text-xs font-bold" data-testid="bulk-tag-add">Add</button>
            <button onClick={() => bulkTagApply(true)} className="h-7 px-2 bg-blue-800 text-white rounded text-xs font-bold" data-testid="bulk-tag-remove">Remove</button>
          </div>
          {canBulkCompliance && (
            <button onClick={bulkComplianceClear}
                    className="h-7 px-3 bg-white text-emerald-700 border border-emerald-400 rounded text-xs font-bold flex items-center gap-1"
                    data-testid="bulk-compliance-clear-btn">
              ✓ Stamp Compliance Cleared
            </button>
          )}
          <button onClick={() => exportCSV(true)} className="ml-auto h-7 px-3 bg-white text-[#005289] rounded text-xs font-bold flex items-center gap-1" data-testid="bulk-export-btn">
            <Download className="w-3 h-3" /> Export Selected CSV
          </button>
        </div>
      )}

      {/* AI Assistant Sidebar */}
      {aiOpen && (
        <div className="fixed inset-0 z-50 flex" data-testid="pipeline-ai-modal">
          <div className="flex-1 bg-slate-900/60" onClick={() => setAiOpen(false)} />
          <div className="w-full max-w-md bg-white shadow-2xl h-full flex flex-col">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "#7C3AED" }}>
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900">Pipeline AI Assistant</div>
                  <div className="text-[10px] text-slate-500">Claude Sonnet · scoped to visible contracts</div>
                </div>
              </div>
              <button onClick={() => setAiOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="pipeline-ai-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3 flex-1 overflow-auto">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Try a prompt</div>
              <div className="space-y-2">
                {AI_PROMPTS.map((p, i) => (
                  <button key={i} onClick={() => { setAiQuery(p); askAI(p); }}
                          className="w-full text-left text-xs p-2.5 border border-slate-200 rounded hover:bg-slate-50 hover:border-[#7C3AED]"
                          data-testid={`pipeline-ai-prompt-${i}`}>
                    {p}
                  </button>
                ))}
              </div>
              {aiAnswer && (
                <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded" data-testid="pipeline-ai-answer">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#7C3AED] mb-1.5">AI Response</div>
                  <div className="text-xs text-slate-800 whitespace-pre-wrap">{aiAnswer}</div>
                </div>
              )}
              {aiBusy && (
                <div className="p-3 text-xs text-slate-500 text-center border border-dashed border-slate-300 rounded">Thinking…</div>
              )}
            </div>
            <div className="p-3 border-t border-slate-200 flex gap-2">
              <input value={aiQuery} onChange={e => setAiQuery(e.target.value)}
                     onKeyDown={e => e.key === "Enter" && askAI()}
                     placeholder="Ask about the pipeline..."
                     className="flex-1 h-9 px-3 text-sm border border-slate-200 rounded"
                     data-testid="pipeline-ai-input" />
              <button onClick={() => askAI()} disabled={aiBusy || !aiQuery.trim()}
                      className="h-9 px-3 rounded text-white text-xs font-semibold flex items-center gap-1 disabled:opacity-60"
                      style={{ background: "#7C3AED" }} data-testid="pipeline-ai-send">
                <Send className="w-3 h-3" /> Ask
              </button>
            </div>
          </div>
        </div>
      )}

      {view === "grid" ? (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="hcd-table">
            <thead>
              <tr>
                {canBulk && (
                  <th style={{ width: 30 }}>
                    <input type="checkbox" checked={selectedCount > 0 && selectedCount === filtered.length}
                           onChange={toggleAll} data-testid="bulk-select-all" />
                  </th>
                )}
                <th>#</th><th>Vendor</th><th>Description</th><th>Department</th><th>Timeline</th>
                <th>Received</th><th>Stage</th><th className="num">Days</th>
                <th className="num">Analyst d</th><th className="num">Attorney d</th><th className="num">GC d</th><th className="num">Vendor d</th>
                <th>SLA</th><th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} onClick={() => nav(`/contract/${c.id}`)} className="cursor-pointer" data-testid={`grid-row-${c.contract_number}`}>
                  {canBulk && (
                    <td onClick={e => e.stopPropagation()} style={{ width: 30 }}>
                      <input type="checkbox" checked={!!selected[c.id]} onChange={e => toggleRow(c.id, e)} data-testid={`bulk-check-${c.contract_number}`} />
                    </td>
                  )}
                  <td className="font-mono text-xs">{c.contract_number}</td>
                  <td className="font-semibold">{c.vendor_legal_name}</td>
                  <td className="text-slate-600 max-w-xs truncate">{c.description}</td>
                  <td>{c.department}</td>
                  <td><span className="chip chip-draft"><span className="dot" />{c.timeline}</span></td>
                  <td className="text-xs">{c.initial_received_date ? new Date(c.initial_received_date).toLocaleDateString() : "—"}</td>
                  <td>{STAGE_LABELS[c.stage]}</td>
                  <td className="num font-mono">{c.days_total}</td>
                  <td className="num font-mono">{c.days_by_stage?.analyst || 0}</td>
                  <td className="num font-mono">{c.days_by_stage?.attorney || 0}</td>
                  <td className="num font-mono">{c.days_by_stage?.gc || 0}</td>
                  <td className="num font-mono">{c.days_by_stage?.vendor || 0}</td>
                  <td><SLABadge status={c.sla_status} remaining={c.sla_remaining} /></td>
                  <td className="num">{currency(c.contract_amount)}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={canBulk ? 15 : 14} className="text-center py-8 text-slate-500">No contracts match filters.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-4 lg:grid-cols-7 gap-3">
          {STAGES_ORDER.map(stage => (
            <div key={stage} className="kanban-col">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">{STAGE_LABELS[stage]}</div>
                <div className="text-[10px] px-1.5 py-0.5 bg-white rounded font-semibold">{(byStage[stage] || []).length}</div>
              </div>
              {(byStage[stage] || []).map(c => (
                <div key={c.id} className="kanban-card"
                     style={{ borderLeftColor: c.sla_status === "overdue" ? "#EF4444" : c.sla_status === "due_soon" ? "#F59E0B" : "#00AE42" }}
                     onClick={() => nav(`/contract/${c.id}`)}
                     data-testid={`kanban-${c.contract_number}`}>
                  <div className="text-[10px] font-mono text-slate-500">{c.contract_number}</div>
                  <div className="text-xs font-semibold text-slate-900 mt-1 truncate">{c.vendor_legal_name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">{c.department}</div>
                  <div className="mt-2 flex items-center justify-between">
                    <SLABadge status={c.sla_status} remaining={c.sla_remaining} />
                    <span className="text-[10px] text-slate-500">{c.days_total}d</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
